import type React from 'react';
import type { ErrorResponse } from 'react-router';
import { isRouteErrorResponse, Link, useLocation, useParams, useRouteError } from 'react-router';

import { cn, getErrorMessage } from '@april9au/react-common/utils';

import { Button } from '@/components/ui/button';
import { useStrings } from '@/i18n/use-strings';

type StatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
  description?: string;
}) => React.JSX.Element | null;
type CustomStatusHandler = (info: {
  error: ErrorResponse;
  params: Record<string, string | undefined>;
}) => string | null;

export function GeneralErrorBoundary({
  defaultStatusHandler = DefaultErrorHandler,
  statusHandlers,
  unexpectedErrorHandler = UnexpectedErrorHandler,
  className,
}: {
  defaultStatusHandler?: StatusHandler;
  statusHandlers?: Record<number, CustomStatusHandler>;
  unexpectedErrorHandler?: (error: unknown) => React.JSX.Element | null;
  className?: string;
}) {
  const error = useRouteError();
  const params = useParams();

  return (
    <div className={cn('bg-background text-foreground flex min-h-svh flex-1 items-center px-6 sm:px-8', className)}>
      <div className="mx-auto w-full max-w-7xl lg:px-8">
        <div className="relative px-4 sm:px-8 lg:px-12">
          <div className="mx-auto max-w-2xl lg:max-w-5xl">
            {isRouteErrorResponse(error)
              ? defaultStatusHandler({
                  error,
                  params,
                  description: statusHandlers?.[error.status]?.({ error, params }) ?? undefined,
                })
              : unexpectedErrorHandler(error)}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Copy comes from the catalogue like everywhere else. `useStrings()` falls back
 * to the English defaults when the root loader never ran — which is exactly the
 * case an error boundary has to survive — so this is safe here.
 */
function DefaultErrorHandler({ error, description }: { error: ErrorResponse; description?: string }) {
  const { errors } = useStrings();
  const location = useLocation();

  const { title, body } =
    error.status === 404
      ? { title: errors.error_not_found_title, body: errors.error_not_found_body }
      : error.status === 403
        ? { title: errors.error_unauthorised_title, body: errors.error_unauthorised_body }
        : error.status === 500
          ? { title: errors.error_server_title, body: errors.error_server_body }
          : { title: errors.error_generic_title, body: errors.error_generic_body };

  return (
    <div className="flex flex-col items-center">
      <p className="text-base font-semibold opacity-70">{error.status}</p>
      <h1 className="font-bebas mt-4 text-5xl tracking-wide uppercase sm:text-6xl">{title}</h1>

      <div className="flex flex-col items-center gap-3">
        {/* A route may pass a more specific line; otherwise the status decides. */}
        <p className="mt-2 max-w-sm text-center text-base opacity-90">{description ?? body}</p>

        {error.status === 404 && (
          <pre className="rounded border border-white/20 bg-white/10 px-1.5 py-0.5 text-base font-medium break-all whitespace-pre-wrap sm:text-xs">
            {location.pathname}
          </pre>
        )}
      </div>

      {/* The one action on the screen, so it wears the app's primary: SLSF
          yellow at the full 48 px, not the ghosted outline it had. */}
      {error.status === 500 ? (
        <Button variant="brand-yellow" size="xl" onClick={() => window.location.reload()} className="mt-8 max-w-xs">
          {errors.error_reload}
        </Button>
      ) : (
        <Button variant="brand-yellow" size="xl" className="mt-8 max-w-xs" asChild>
          <Link to="/">{errors.error_home}</Link>
        </Button>
      )}
    </div>
  );
}

/** A failed fetch, or a browser that says it is offline. */
function isOffline(error: unknown): boolean {
  const failedToFetch =
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof error.message === 'string' &&
    error.message === 'Failed to fetch';

  return failedToFetch || (typeof navigator !== 'undefined' && !navigator.onLine);
}

function UnexpectedErrorHandler(error: unknown) {
  return <UnexpectedError error={error} />;
}

function UnexpectedError({ error }: { error: unknown }) {
  const { errors } = useStrings();
  const offline = isOffline(error);

  return (
    <div className="flex flex-col items-center">
      <h1 className="font-bebas mt-2 text-5xl tracking-wide uppercase sm:text-6xl">
        {offline ? errors.error_offline_title : errors.error_generic_title}
      </h1>

      <div className="flex flex-col items-center gap-3">
        <p className="mt-4 max-w-sm text-center text-base opacity-90">
          {offline ? errors.error_offline_body : errors.error_generic_body}
        </p>

        {/* The raw message is a developer aid, not copy — never translated. */}
        {offline ? null : (
          <pre className="rounded border border-white/20 bg-white/10 px-1.5 py-0.5 text-base font-medium break-all whitespace-pre-wrap sm:text-xs">
            {getErrorMessage(error)}
          </pre>
        )}
      </div>

      <Button variant="brand-yellow" size="xl" onClick={() => window.location.reload()} className="mt-8 max-w-xs">
        {errors.error_reload}
      </Button>
    </div>
  );
}
